import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { orderBy, partition } from 'lodash'
import { useHistory } from 'react-router-dom'
import ModulePanel from '../../ui/ModulePanel/ModulePanel'
import DataTable from '../../ui/DataTable/DataTable'
import Spinner from '../../ui/Spinner/Spinner'
import StatusMessage, { useStatusMessage } from '../../ui/StatusMessage/StatusMessage'
import { listBlogMaterials, listBlogTags } from '../../api/blog/blogApi'
import {
  listLocalAuthors,
  listLocalMaterials,
  listLocalTags,
  resolveAuthorLabel,
  upsertLocalTag,
} from '../../api/blog/blogLocalStore'
import Field from '../../ui/Field/Field'
import LinksRowActions from '../Marketing/Links/LinksRowActions'
import '../Marketing/Links/LinksTable.sass'
import '../../ui/ActiveStatusBadge/ActiveStatusBadge.sass'
import { FilterGroup } from '../Marketing/Gifts/GiftsFundTableFilters'
import giftStyles from '../Marketing/Gifts/GiftsFund.module.scss'
import { BLOG_STATUS, getBlogStatusLabel } from '../../api/blog/blogConstants'
import { formatDateAsDayMontYearHHMM } from '../../utils/date/dateFormat'
import styles from './Blog.module.scss'
import DeleteBlogMaterialPopUp from './DeleteBlogMaterialPopUp'

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Усі' },
  { value: BLOG_STATUS.draft, label: 'Чернетка' },
  { value: BLOG_STATUS.scheduled, label: 'Заплановано' },
  { value: BLOG_STATUS.unpublished, label: 'Знято' },
]

const STATUS_BADGE_CLASS = {
  [BLOG_STATUS.published]: 'ActiveStatusBadge_active',
  [BLOG_STATUS.scheduled]: 'ActiveStatusBadge_upcoming',
  [BLOG_STATUS.draft]: 'ActiveStatusBadge_archived',
  [BLOG_STATUS.unpublished]: 'ActiveStatusBadge_inactive',
}

const parseIsoToSeconds = (iso) => {
  if (!iso || typeof iso !== 'string') return null
  const ms = Date.parse(iso)
  if (!Number.isFinite(ms)) return null
  return { seconds: Math.floor(ms / 1000) }
}

const mergeMaterials = (remote = [], local = []) => {
  const map = new Map()
  remote.forEach((item) => {
    if (item?.id) map.set(item.id, item)
  })
  local.forEach((item) => {
    if (!item?.id) return
    const existing = map.get(item.id)
    if (!existing || (item.draft && item._serverSynced !== true)) {
      map.set(item.id, {
        ...existing,
        ...item,
        clapCount: existing?.clapCount ?? item.clapCount,
        openCount: existing?.openCount ?? item.openCount,
        readCount: existing?.readCount ?? item.readCount,
        bookmarkCount: existing?.bookmarkCount ?? item.bookmarkCount,
        shareCount: existing?.shareCount ?? item.shareCount,
      })
    }
  })
  return Array.from(map.values())
}

const renderStatusBadge = (status) => {
  const variantClass = STATUS_BADGE_CLASS[status] || STATUS_BADGE_CLASS[BLOG_STATUS.draft]
  return <span className={`ActiveStatusBadge ${variantClass}`.trim()}>{getBlogStatusLabel(status)}</span>
}

function BlogMaterialsList() {
  const history = useHistory()
  const [statusMessage, setStatusMessage] = useStatusMessage()
  const [materials, setMaterials] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [deleteMaterial, setDeleteMaterial] = useState(null)
  const deletedIdsRef = useRef(new Set())
  const loadInFlightRef = useRef(false)
  const authors = useMemo(() => listLocalAuthors(), [])
  const [tagNames, setTagNames] = useState(() => {
    const map = {}
    listLocalTags().forEach((tag) => {
      map[tag.id] = tag.name
    })
    return map
  })
  const tagsById = tagNames

  const load = useCallback(async () => {
    if (loadInFlightRef.current) return
    loadInFlightRef.current = true
    setIsLoading(true)
    setLoadError('')
    try {
      const local = await listLocalMaterials()
      setMaterials((current) => (current.length ? current : local))
      const [remote, remoteTags] = await Promise.all([listBlogMaterials({ limit: 100 }), listBlogTags()])
      if (remoteTags.ok) remoteTags.tags.forEach((tag) => upsertLocalTag(tag))
      if (remote.ok) {
        const merged = mergeMaterials(remote.materials, await listLocalMaterials())
        setMaterials(merged.filter((item) => !deletedIdsRef.current.has(item.id)))
      } else {
        setLoadError('Не вдалося завантажити матеріали.')
        setMaterials((current) => (current.length ? current : local))
        if (!local.length) {
          setStatusMessage({
            show: true,
            type: 'fail',
            message: 'Не вдалося оновити матеріали. Спробуйте ще раз.',
            closeAfter: 4000,
          })
        }
      }
      const map = {}
      listLocalTags().forEach((tag) => {
        map[tag.id] = tag.name
      })
      setTagNames(map)
    } catch (error) {
      setLoadError('Не вдалося завантажити матеріали.')
    } finally {
      setIsLoading(false)
      loadInFlightRef.current = false
    }
  }, [setStatusMessage])

  useEffect(() => {
    load()
    const refresh = () => {
      if (document.visibilityState === 'visible') void load()
    }
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [load])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return materials.filter((item) => {
      if (!q) return true
      const title = String(item.title || item.draft?.title || '').toLowerCase()
      const slug = String(item.slug || item.draft?.slug || '').toLowerCase()
      return title.includes(q) || slug.includes(q)
    })
  }, [materials, query])

  const rows = useMemo(
    () =>
      filtered.map((item) => {
        const draft = item.draft || {}
        const tagIds = Array.isArray(draft.tagIds) ? draft.tagIds : Array.isArray(item.tagIds) ? item.tagIds : []
        const tagLabels = tagIds.map((id) => tagsById[id] || id).filter(Boolean)
        return {
          ...item,
          id: item.id,
          title: draft.title || item.title || 'Без назви',
          status: item.status || BLOG_STATUS.draft,
          authorId: draft.authorId || item.authorId || null,
          authorLabel: resolveAuthorLabel(draft.authorId || item.authorId, authors),
          categoryLabel: tagsById[draft.categoryId || item.categoryId] || '—',
          tagLabels,
          publishedAt: item.publishedAt || draft.publishedAt || null,
          openCount: item.openCount,
          readCount: item.readCount,
          clapCount: item.clapCount,
          bookmarkCount: item.bookmarkCount,
          shareCount: item.shareCount,
        }
      }),
    [filtered, authors, tagsById]
  )

  const columns = useMemo(
    () => [
      {
        field: 'status',
        headerClassName: 'TableHeadCol',
        headerName: 'Статус',
        width: 140,
        renderCell: (params) => renderStatusBadge(params.row.status),
      },
      {
        field: 'title',
        headerClassName: 'TableHeadCol',
        headerName: 'Назва',
        minWidth: 220,
        flex: 1.2,
        cellClassName: styles.centeredCell,
        renderCell: (params) => <span className="MarketingLinks-TitleCell">{params.row.title || '—'}</span>,
      },
      {
        field: 'authorLabel',
        headerClassName: 'TableHeadCol',
        headerName: 'Автор',
        width: 140,
      },
      {
        field: 'categoryLabel',
        headerClassName: 'TableHeadCol',
        headerName: 'Категорія',
        width: 150,
      },
      {
        field: 'tagLabels',
        headerClassName: 'TableHeadCol',
        headerName: 'Теги',
        minWidth: 180,
        flex: 1,
        sortable: false,
        cellClassName: styles.centeredCell,
        renderCell: (params) => {
          const tags = Array.isArray(params.row.tagLabels) ? params.row.tagLabels : []
          if (!tags.length) {
            return <span className={styles.tagsEmpty}>—</span>
          }
          return (
            <span className="MarketingLinks-ContextCell">
              {tags.map((label) => (
                <span key={label} className="MarketingLinks-ContextChip" title={label}>
                  {label}
                </span>
              ))}
            </span>
          )
        },
      },
      {
        field: 'publishedAt',
        headerClassName: 'TableHeadCol',
        headerName: 'Дата публікації',
        width: 160,
        type: 'number',
        valueGetter: (params) => Date.parse(params.row.publishedAt) || 0,
        renderCell: (params) => {
          const ts = parseIsoToSeconds(params.row.publishedAt)
          return ts ? formatDateAsDayMontYearHHMM(ts) : '—'
        },
      },
      {
        field: 'openCount',
        headerClassName: 'TableHeadCol',
        headerName: 'Відкриття',
        type: 'number',
        width: 100,
        renderCell: (params) => (Number.isInteger(params.row.openCount) ? params.row.openCount : '—'),
      },
      {
        field: 'readCount',
        headerClassName: 'TableHeadCol',
        headerName: 'Прочитання',
        type: 'number',
        width: 110,
        renderCell: (params) => (Number.isInteger(params.row.readCount) ? params.row.readCount : '—'),
      },
      {
        field: 'clapCount',
        headerClassName: 'TableHeadCol',
        headerName: 'Оплески',
        type: 'number',
        width: 90,
        renderCell: (params) => (Number.isInteger(params.row.clapCount) ? params.row.clapCount : '—'),
      },
      {
        field: 'bookmarkCount',
        headerClassName: 'TableHeadCol',
        headerName: 'Закладки',
        description: 'Кількість користувачів, у яких стаття зараз у закладках.',
        width: 100,
        type: 'number',
        renderCell: (params) => (Number.isInteger(params.row.bookmarkCount) ? params.row.bookmarkCount : '—'),
      },
      {
        field: 'shareCount',
        headerClassName: 'TableHeadCol',
        headerName: 'Поширення',
        description: 'Копіювання посилання або вибір способу поширення. Відкриття меню не враховується.',
        width: 110,
        type: 'number',
        renderCell: (params) => (Number.isInteger(params.row.shareCount) ? params.row.shareCount : '—'),
      },
      {
        field: 'actions',
        headerClassName: 'TableHeadCol',
        headerName: '',
        width: 56,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <LinksRowActions
            onEdit={() => history.push(`/blog/materials/${params.row.id}`)}
            onDelete={() => setDeleteMaterial(params.row)}
          />
        ),
      },
    ],
    [history]
  )

  const [publishedRows, unpublishedRows] = useMemo(() => partition(rows, { status: BLOG_STATUS.published }), [rows])
  const draftRows = useMemo(
    () =>
      orderBy(
        unpublishedRows.filter((row) => !statusFilter || row.status === statusFilter),
        [(row) => row.status === BLOG_STATUS.draft, (row) => Date.parse(row.createdAt) || 0, 'id'],
        ['desc', 'desc', 'asc']
      ),
    [unpublishedRows, statusFilter]
  )
  const publicationRows = useMemo(
    () => orderBy(publishedRows, [(row) => Date.parse(row.publishedAt) || 0, 'id'], ['desc', 'asc']),
    [publishedRows]
  )
  const draftColumns = useMemo(
    () =>
      columns.map((column) =>
        column.field === 'publishedAt'
          ? {
              ...column,
              field: 'createdAt',
              headerName: 'Дата створення',
              valueGetter: (params) => Date.parse(params.row.createdAt) || 0,
              renderCell: (params) => {
                const ts = parseIsoToSeconds(params.row.createdAt)
                return ts ? formatDateAsDayMontYearHHMM(ts) : '—'
              },
            }
          : column
      ),
    [columns]
  )

  const renderTable = (sectionRows, published = false) =>
    isLoading && sectionRows.length === 0 ? (
      <div className={styles.empty} role="status" aria-live="polite">
        <Spinner type="module" />
        <p className={styles.loadingLabel}>Завантажуємо матеріали…</p>
      </div>
    ) : sectionRows.length === 0 ? (
      <p className={styles.empty}>
        {loadError || (published ? 'Немає опублікованих матеріалів.' : 'Немає неопублікованих матеріалів.')}
      </p>
    ) : (
      <div className={`${styles.table} MarketingLinks-Table`}>
        <DataTable
          initialState={published ? { sorting: { sortModel: [{ field: 'publishedAt', sort: 'desc' }] } } : undefined}
          table={{
            rows: sectionRows,
            columns: published ? columns : draftColumns,
            autoHeight: true,
            disableRowSelectionOnClick: true,
            onRowDoubleClick: (params) => history.push(`/blog/materials/${params.row.id}`),
          }}
        />
      </div>
    )

  const sectionTitle = (title, sectionRows) =>
    `${title}${isLoading && sectionRows.length === 0 ? '' : ` (${sectionRows.length})`}`
  const refreshIndicator = (sectionRows) =>
    isLoading && sectionRows.length > 0 ? (
      <span role="status" aria-label="Оновлюємо матеріали">
        <Spinner type="button" theme="gray" />
      </span>
    ) : null

  return (
    <>
      {deleteMaterial ? (
        <DeleteBlogMaterialPopUp
          material={deleteMaterial}
          closePopUp={() => setDeleteMaterial(null)}
          onDeleted={(id) => {
            deletedIdsRef.current.add(id)
            setMaterials((current) => current.filter((item) => item.id !== id))
            setStatusMessage({ show: true, type: 'success', message: 'Матеріал видалено', closeAfter: 5000 })
          }}
        />
      ) : null}
      {statusMessage.show ? (
        <StatusMessage className="Site-StatusMessage" type={statusMessage.type} message={statusMessage.message} />
      ) : null}
      <div className={styles.materialsStack}>
        <ModulePanel
          className={styles.panel}
          title={sectionTitle('Чернетки та неопубліковані матеріали', draftRows)}
          buttons={refreshIndicator(draftRows)}
        >
          <div className={giftStyles.filters}>
            <div className={giftStyles.searchGroup}>
              <span className={giftStyles.filtersLabel}>Пошук</span>
              <div className={giftStyles.searchField}>
                <Field
                  fieldType="input"
                  config={{ label: 'Назва або slug', type: 'text', placeholder: 'Назва або slug' }}
                  value={query}
                  changed={(event) => setQuery(event.target.value)}
                  isChangable
                />
              </div>
            </div>
            <FilterGroup
              label="Статус"
              value={statusFilter}
              options={STATUS_FILTER_OPTIONS}
              onChange={setStatusFilter}
            />
          </div>
          {renderTable(draftRows)}
        </ModulePanel>
        <ModulePanel
          className={styles.panel}
          title={sectionTitle('Опубліковані матеріали', publicationRows)}
          buttons={refreshIndicator(publicationRows)}
        >
          {renderTable(publicationRows, true)}
        </ModulePanel>
      </div>
    </>
  )
}

export default BlogMaterialsList
